// src/pages/NewInformation.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import {
  Box,
  Container,
  Typography,
  Divider,
  Stack,
  Chip,
  Link as MLink,
  IconButton,
  Breadcrumbs,
  Link,
  useMediaQuery,
  CircularProgress,
  Button,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import HeroWave from "../../../GlobalFunction/HeroWave";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { apiGetNewsGrouped } from "../../../../api/News";
import { resolveWpAssetUrl } from "../../../../lib/http";
import handwashGuide from "../../../../data/handwashGuide.json";

/* ---------------- multilingual source data ---------------- */
const I18N = {
  infoSection: {
    ZH: "衛生資訊",
    EN: "Health Information",
    JP: "衛生情報コラム",
  },
  latestNews: {
    ZH: "最新消息",
    EN: "Latest News",
    JP: "ニュース(新着)",
  },
  breadcrumbHome: {
    ZH: "首頁",
    EN: "Home",
    JP: "ホーム",
  },
  breadcrumbInfo: {
    ZH: "最新資訊",
    EN: "Information",
    JP: "最新情報",
  },
  seeMore: {
    ZH: "看更多",
    EN: "See More",
    JP: "もっと見る",
  },
  eventNews: {
    ZH: "活動資訊",
    EN: "Event Information",
    JP: "イベント情報",
  },
};

const safeSeg = (s) => encodeURIComponent(String(s || "").trim());
const buildNewsHref = (newsType, slug) =>
  `/最新資訊/${safeSeg(newsType)}/${safeSeg(slug)}`;

/* ---------------- date helpers ---------------- */
function parseDateOnlyToDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T00:00:00`);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatPostDate(value) {
  const d = parseDateOnlyToDate(value);
  if (!d) return value ? String(value) : "";

  const year = String(d.getFullYear());
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}/${month}/${day}`;
}

// ✅ NOTE: after the API update, backend already returns public URLs
// (normalizeNewsRow uses resolvePublicUrl), so we can prefer backend url directly.
// But we keep a safe fallback for older payloads.
const WP_ORIGIN = (
  process.env.REACT_APP_WP_ORIGIN || "http://localhost/wp-headless"
).replace(/\/+$/, "");

function resolveAssetUrl(url) {
  if (!url) return "";
  const raw = String(url).trim();
  if (!raw) return "";

  // absolute URL -> keep it (do NOT rewrite localhost)
  if (/^https?:\/\//i.test(raw)) return raw;

  // relative -> prefix with WP origin
  if (raw.startsWith("/")) return `${WP_ORIGIN}${raw}`;
  return `${WP_ORIGIN}/${raw}`;
}

function pickThumbUrl(n) {
  const raw = n?.thumbnail?.url || n?.thumbnail?.asset?.url || "";
  return resolveWpAssetUrl(raw);
}

/**
 * ✅ UPDATED:
 * After backend localization update:
 * - tags could be array of strings (grouped endpoint), OR
 * - tags could be objects ({ name: "..." }) (other endpoints)
 * We support both safely.
 */
function firstTagName(n) {
  const tags = Array.isArray(n?.tags) ? n.tags : [];
  const t0 = tags[0];

  if (!t0) return "";
  if (typeof t0 === "string") return t0.trim();

  if (typeof t0 === "object") {
    return String(
      t0?.name ?? t0?.label ?? t0?.tagName ?? t0?.title ?? "",
    ).trim();
  }

  return "";
}

function getHandwashGuideArticle(lang) {
  const title =
    handwashGuide?.title?.[lang] ||
    handwashGuide?.title?.ZH ||
    handwashGuide?.title?.EN ||
    "";

  return {
    id: "manual-handwash-guide",
    image: handwashGuide?.thumbnail?.image || "/img/news/healthTop.png",
    to: buildNewsHref("衛生資訊", "hand-sanitizing-manual"),
    date: "",
    title,
    summary: "",
    tag:
      lang === "EN" ? "Hand Hygiene" : lang === "JP" ? "手指衛生" : "手部衛生",
    isManual: true,
  };
}

function isHandwashDbArticle(n) {
  const text = [
    n?.slug,
    n?.title,
    n?.titleZH,
    n?.titleEN,
    n?.titleJP,
    n?.newsSlug,
    n?.code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (handwashGuide.match || []).some((keyword) =>
    text.includes(String(keyword || "").toLowerCase()),
  );
}

function SectionTitle({ labelKey, to, color, lang }) {
  const TitleComp = to ? RouterLink : "span";

  return (
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      sx={{ mb: 0.75 }}
    >
      <Typography
        component={TitleComp}
        to={to}
        variant="h6"
        sx={{
          fontWeight: 700,
          fontFamily: "LINE Sans TW",
          color,
          letterSpacing: 0.5,
          cursor: to ? "pointer" : "default",
          textDecoration: "none",
        }}
      >
        {I18N[labelKey][lang]}
      </Typography>

      {to && (
        <Button
          component={RouterLink}
          to={to}
          variant="contained"
          endIcon={<KeyboardArrowRightIcon />}
          sx={{
            px: 1.5,
            py: 0.25,
            borderRadius: 0,
            clipPath:
              "polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)",
            bgcolor: "grey",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            whiteSpace: "nowrap",
            boxShadow: "none",

            display: "inline-flex",
            alignItems: "center",

            transition: "all 0.2s ease",

            "&:hover": {
              bgcolor: getPrimaryColor("b2b"),
              filter: "brightness(0.95)",
              boxShadow: "none",
            },

            "& .MuiButton-endIcon": {
              ml: 0.5,
            },
          }}
        >
          {I18N.seeMore[lang]}
        </Button>
      )}
    </Stack>
  );
}

function BgTile({ item, scale = 1, sx }) {
  return (
    <Box
      component={RouterLink}
      to={item.to}
      title={item.title}
      sx={{
        position: "relative",
        display: "block",
        borderRadius: 2,
        overflow: "hidden",
        textDecoration: "none",
        backgroundImage: `url("${item.image}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        border: "1px solid rgba(255,255,255,.25)",
        transform: `scale(${scale})`,
        transformOrigin: "center",
        transition: "transform .25s ease, box-shadow .18s ease",
        ...sx,
        "&:after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,.55), rgba(0,0,0,.25) 40%, rgba(0,0,0,.1) 70%, rgba(0,0,0,0))",
        },
        "&:hover": {
          transform: `scale(${scale}) translateY(-2px)`,
          boxShadow: "0 12px 26px rgba(0,0,0,.3)",
        },
      }}
    >
      <Stack
        spacing={0.5}
        sx={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 10,
          color: "#fffffc",
          zIndex: 1,
          textShadow: "0 2px 6px rgba(0,0,0,.85)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {item.tag && (
            <Chip
              size="small"
              label={item.tag}
              sx={{
                bgcolor: "rgba(0,174,139,.95)",
                color: "#fffffc",
                fontWeight: 700,
              }}
            />
          )}
          {item.date && (
            <Typography variant="caption" sx={{ opacity: 0.95 }}>
              {item.date}
            </Typography>
          )}
        </Stack>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.15 }}
          noWrap
        >
          {item.title}
        </Typography>

        {item.summary && (
          <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
            {item.summary}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function TriCarousel({
  items = [],
  interval = 4500,
  rowHeight = { xs: 170, md: 220 },
}) {
  const n = items.length;
  const isMobile = useMediaQuery("(max-width:600px)");

  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx((i) => (n ? i % n : 0)), [n]);

  const next = useCallback(() => {
    if (n > 0) setIdx((i) => (i + 1) % n);
  }, [n]);
  const prev = useCallback(() => {
    if (n > 0) setIdx((i) => (i + n - 1) % n);
  }, [n]);

  const timerRef = useRef(null);
  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const start = useCallback(() => {
    clear();
    if (n > 1) timerRef.current = setInterval(next, interval);
  }, [clear, n, next, interval]);
  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  const startX = useRef(0);
  const dragging = useRef(false);
  const onDown = (e) => {
    dragging.current = true;
    startX.current = "touches" in e ? e.touches[0].clientX : e.clientX;
    clear();
  };
  const onUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    const endX =
      "changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
    const dx = endX - startX.current;
    if (Math.abs(dx) > 40) dx < 0 ? next() : prev();
    start();
  };

  if (n === 0) return null;

  const mid = items[idx % n];
  const left = n > 1 ? items[(idx + n - 1) % n] : null;
  const right = n > 2 ? items[(idx + 1) % n] : null;

  return (
    <Box
      sx={{ position: "relative", userSelect: "none", overflow: "visible" }}
      onMouseEnter={clear}
      onMouseLeave={start}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onTouchStart={onDown}
      onTouchEnd={onUp}
    >
      {n > 1 && (
        <>
          <IconButton
            onClick={prev}
            sx={{
              position: "absolute",
              left: { xs: 4, sm: -14, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,.45)",
              color: "#fffffc",
              "&:hover": { bgcolor: "rgba(0,0,0,.65)" },
              zIndex: 3,
            }}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <IconButton
            onClick={next}
            sx={{
              position: "absolute",
              right: { xs: 4, sm: -14, md: -28 },
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,.45)",
              color: "#fffffc",
              "&:hover": { bgcolor: "rgba(0,0,0,.65)" },
              zIndex: 3,
            }}
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </>
      )}

      {isMobile ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr" }}>
          <BgTile
            item={mid}
            scale={1.0}
            sx={{ height: rowHeight, width: "100%" }}
          />
        </Box>
      ) : n === 1 ? (
        <Box
          sx={{
            width: "100%",
            mx: "auto",
            display: "grid",
            gridTemplateColumns: "1fr",
          }}
        >
          <BgTile
            item={mid}
            scale={1}
            sx={{ height: rowHeight, width: "100%" }}
          />
        </Box>
      ) : n === 2 ? (
        <Box
          sx={{
            width: "100%",
            mx: "auto",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <BgTile
            item={items[idx % n]}
            scale={1.0}
            sx={{ height: rowHeight }}
          />
          <BgTile
            item={items[(idx + 1) % n]}
            scale={1.0}
            sx={{ height: rowHeight }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "0.9fr 1.4fr 0.9fr",
              md: "0.95fr 1.7fr 0.95fr",
            },
            gap: 2,
            alignItems: "center",
            overflow: "visible",
          }}
        >
          <BgTile
            item={left}
            scale={0.9}
            sx={{ height: rowHeight, width: "100%" }}
          />
          <BgTile
            item={mid}
            scale={1.08}
            sx={{ height: rowHeight, width: "100%", zIndex: 2 }}
          />
          <BgTile
            item={right}
            scale={0.9}
            sx={{ height: rowHeight, width: "100%" }}
          />
        </Box>
      )}
    </Box>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */
export default function NewInformation({ mode }) {
  const location = useLocation();

  const [lang, setLang] = useState(() => getLangFromCookie() || "EN");

  const [loading, setLoading] = useState(false);
  const [grouped, setGrouped] = useState({});

  useEffect(() => {
    const handleLangChange = () => {
      const next = getLangFromCookie() || "EN";
      setLang(next);
    };
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  // ✅ IMPORTANT UPDATE:
  // - backend now localizes title and tag names by lang
  // - src/api/news.js always attaches ?lang=...
  // So we MUST re-fetch when lang changes to update displayed titles/tags.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await apiGetNewsGrouped({ limitPerType: 12 });
        if (!alive) return;
        setGrouped(res?.grouped || {});
      } catch (e) {
        console.error("apiGetNewsGrouped failed:", e);
        if (!alive) return;
        setGrouped({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [lang]); // ✅ re-fetch on lang change

  // grouped keys are the canonical ZH types (衛生資訊/最新消息/活動資訊)
  const healthList = grouped["衛生資訊"] || [];
  const latestList = grouped["最新消息"] || [];
  const eventList = grouped["活動資訊"] || [];

  const healthArticles = useMemo(() => {
    const manualArticle = getHandwashGuideArticle(lang);
    const hasManualFromDb = healthList.some((n) => isHandwashDbArticle(n));

    const dbArticles = healthList.map((n) => {
      const isHandwash = isHandwashDbArticle(n);
      const customThumb = handwashGuide?.thumbnail?.image;

      return {
        id: `${n.newsType}-${n.slug}`,
        image:
          isHandwash && customThumb
            ? customThumb
            : pickThumbUrl(n) || "/img/news/pressReleaseTop.png",
        to: buildNewsHref(n.newsType, n.slug),
        date: formatPostDate(n.postDate),
        title: n.title || "",
        summary: "",
        tag: firstTagName(n),
      };
    });

    return hasManualFromDb ? dbArticles : [manualArticle, ...dbArticles];
  }, [healthList, lang]);

  const eventArticles = useMemo(() => {
    return eventList.map((n) => ({
      id: `${n.newsType}-${n.slug}`,
      image: pickThumbUrl(n) || "/img/news/event-電潔水.png",
      to: buildNewsHref(n.newsType, n.slug),
      date: formatPostDate(n.postDate),
      title: n.title || "",
      summary: "",
      tag: firstTagName(n) || "活動", // optional fallback
    }));
  }, [eventList, lang]);

  const showHealthSection = loading || healthArticles.length > 0;
  const showLatestSection = loading || latestList.length > 0;
  const showEventSection = loading || eventArticles.length > 0;

  // optional: ensure rerender on route transitions
  const _routeKey = location?.key || "information";

  return (
    <Box
      key={_routeKey}
      sx={{
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        py: { xs: 4, md: 6 },
      }}
    >
      <HeroWave image={"/img/news/pressReleaseTop.png"} h3={"最新資訊"} />

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link underline="hover" color="inherit" href={"/"}>
            {I18N.breadcrumbHome[lang]}
          </Link>
          <Typography color="text.primary">
            {I18N.breadcrumbInfo[lang]}
          </Typography>
        </Breadcrumbs>
      </Container>

      <Container maxWidth="lg">
        {/* 衛生資訊 */}
        {showHealthSection && (
          <>
            <SectionTitle
              labelKey="infoSection"
              lang={lang}
              to={"/最新資訊/衛生資訊"}
              color={getPrimaryColor(mode)}
            />
            <Divider
              sx={{
                mb: 2,
                borderColor: getPrimaryColor(mode),
                borderBottomWidth: 3,
                opacity: 0.95,
              }}
            />

            {loading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TriCarousel
                items={healthArticles}
                interval={4200}
                rowHeight={{ xs: 180, md: 230 }}
              />
            )}
          </>
        )}

        {/* 最新消息 */}
        {showLatestSection && (
          <>
            <Box sx={{ mt: { xs: 4, md: 6 } }} />
            <SectionTitle
              labelKey="latestNews"
              lang={lang}
              to={"/最新資訊/最新消息"}
              color={getPrimaryColor(mode)}
            />
            <Divider
              sx={{
                mb: 1.5,
                borderColor: getPrimaryColor(mode),
                borderBottomWidth: 3,
                opacity: 0.95,
              }}
            />

            {loading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  backdropFilter: "blur(2px)",
                }}
              >
                {(latestList || []).slice(0, 4).map((n, i, arr) => {
                  const tagLabel = firstTagName(n);
                  const href = buildNewsHref(n.newsType, n.slug);

                  return (
                    <React.Fragment key={`${n.newsType}-${n.slug}-${i}`}>
                      <Box
                        component={RouterLink}
                        to={href}
                        sx={{
                          display: "block",
                          textDecoration: "none",
                          color: "inherit",
                          px: 1.5,
                          py: 1.25,
                          cursor: "pointer",

                          "&, &:visited, &:active, &:hover": {
                            textDecoration: "none",
                          },

                          "& .news-row-date": {
                            color: "text.secondary",
                            transition: "color 0.2s ease",
                          },

                          "& .news-row-titlewrap": {
                            color: "#000000",
                            transition: "color 0.2s ease",
                          },

                          "&:hover .news-row-date": {
                            color: getPrimaryColor("b2b"),
                          },

                          "&:hover .news-row-titlewrap": {
                            color: getPrimaryColor("b2b"),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: { xs: "block", sm: "flex" },
                            gap: { sm: 2 },
                            alignItems: { sm: "center" },
                            width: "100%",
                          }}
                        >
                          <Typography
                            className="news-row-date"
                            sx={{
                              display: "block",
                              mb: { xs: 0.5, sm: 0 },
                              minWidth: { xs: "auto", sm: 110, },
                              width: { xs: "auto", sm: 110, },
                              flexShrink: 0,
                              opacity: 0.9,
                              fontSize: { xs: 13.5, sm: 15 },
                              lineHeight: 1.5,
                              letterSpacing: "0.04em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatPostDate(n.postDate)}
                          </Typography>

                          <Box
                            className="news-row-titlewrap"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1.5,
                              lineHeight: 1.5,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {tagLabel ? (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 1,
                                  height: 22,
                                  bgcolor: getPrimaryColor(mode),
                                  color: "#fffffc",
                                  fontWeight: 400,
                                  fontSize: 15,
                                  lineHeight: "22px",
                                  flexShrink: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {tagLabel}
                              </Box>
                            ) : null}

                            <Box
                              component="span"
                              sx={{
                                minWidth: 0,
                                color: "inherit",
                                fontWeight: 400,
                                fontSize: { xs: 13.5, sm: 15 },
                                lineHeight: 1.6,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {n.title}
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {i < arr.length - 1 && (
                        <Divider
                          sx={{
                            mx: 1.5,
                            borderColor: "rgba(0,0,0,0.08)",
                            borderBottomWidth: 1,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {/* 活動資訊 */}
        {showEventSection && (
          <>
            <Box sx={{ mt: { xs: 4, md: 6 } }} />
            <SectionTitle
              labelKey="eventNews"
              lang={lang}
              to={"/最新資訊/活動資訊"}
              color={getPrimaryColor(mode)}
            />
            <Divider
              sx={{
                mb: 2,
                borderColor: getPrimaryColor(mode),
                borderBottomWidth: 3,
                opacity: 0.95,
              }}
            />

            {loading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TriCarousel
                items={eventArticles}
                interval={4600}
                rowHeight={{ xs: 180, md: 230 }}
              />
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
